import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * Google Consent Mode v2 banner.
 *
 * - Persists the visitor's choice in localStorage under `hinton-consent-v1`.
 * - Updates Google's consent state via `window.gtag('consent', 'update', ...)`
 *   so AdSense / GA respect the choice in real time (no reload needed).
 * - The default consent state is set in `public/index.html` BEFORE
 *   adsbygoogle.js loads — this component only handles the user choice.
 * - Hidden on `/admin`, `/login`, `/accept-invite/*` (editorial surfaces).
 * - Suppressed on routes hosting LegacyRedirect (catch-all) is fine: the
 *   page redirects before the banner is meaningful.
 *
 * Exposes `window.openCookieSettings()` so the footer "Cookie settings" link
 * (or anything else) can re-open the banner for a second-thought visitor.
 */
const STORAGE_KEY = "hinton-consent-v1";

const SUPPRESSED_PREFIXES = ["/admin", "/login", "/accept-invite"];

function pathIsSuppressed(pathname) {
  return SUPPRESSED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

function applyConsent(choice) {
  const state = choice === "accept"
    ? { ad_storage: "granted", ad_user_data: "granted", ad_personalization: "granted", analytics_storage: "granted" }
    : { ad_storage: "denied",  ad_user_data: "denied",  ad_personalization: "denied",  analytics_storage: "denied" };
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", state);
  }
}

export default function CookieConsent() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Suppress on editorial / admin routes.
    if (pathIsSuppressed(window.location.pathname)) return;
    // Show only if no choice has been made yet.
    let saved = null;
    try { saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null"); } catch { /* empty */ }
    if (!saved || !saved.choice) setOpen(true);

    // Expose a global re-open hook for the footer / preference link.
    window.openCookieSettings = () => setOpen(true);
    return () => { try { delete window.openCookieSettings; } catch { /* empty */ } };
  }, []);

  const choose = (choice) => {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, at: new Date().toISOString() }),
      );
    } catch { /* empty */ }
    applyConsent(choice);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:px-6 sm:pb-6 print:hidden"
      data-testid="cookie-consent-banner"
    >
      <div className="mx-auto max-w-5xl bg-[#111] text-[#F9F9F7] shadow-[0_-12px_40px_-12px_rgba(0,0,0,0.5)] border border-white/10">
        <div className="grid md:grid-cols-[1fr_auto] gap-5 md:gap-8 px-5 py-5 md:px-7 md:py-6 items-start md:items-center">
          <div className="space-y-2">
            <div className="overline text-[var(--hinton-red)]">Cookies on Hinton<span className="text-white">.</span></div>
            <p className="text-sm md:text-[0.95rem] leading-relaxed text-neutral-200">
              We use cookies to deliver our editorial, measure performance, and with your permission serve relevant advertising via Google AdSense. You can change your mind at any time from our{" "}
              <Link to="/cookies" className="underline underline-offset-4 hover:text-white">cookie policy</Link>.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row gap-2 sm:gap-2.5 md:min-w-[220px]">
            <button
              type="button"
              onClick={() => choose("reject")}
              className="px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold border border-white/40 text-white hover:bg-white/10 transition-colors"
              data-testid="cookie-reject"
            >
              Reject all
            </button>
            <button
              type="button"
              onClick={() => choose("accept")}
              className="px-5 py-3 text-[11px] uppercase tracking-[0.22em] font-bold bg-[var(--hinton-red)] text-white hover:brightness-110 transition-[filter]"
              data-testid="cookie-accept"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
